import { useCreateOrUpdateDocument } from '@/hooks/queries/documents';
import { DocumentDataType } from '@/types/doc';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from '@nextui-org/react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import * as z from 'zod';

const documentEditSchema = z.object({
  filename: z.string().trim().min(1, 'Document name is required'),
});

type DocumentEditFormData = z.infer<typeof documentEditSchema>;

interface DocumentEditDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  document: DocumentDataType;
  onSuccess?: () => void;
}

const DocumentEditDrawer = ({
  isOpen,
  onClose,
  document,
  onSuccess,
}: DocumentEditDrawerProps) => {
  const {
    register,
    handleSubmit: handleFormSubmit,
    reset,
    formState: { errors },
  } = useForm<DocumentEditFormData>({
    resolver: zodResolver(documentEditSchema),
    defaultValues: {
      filename: document?.filename || '',
    },
  });

  const { mutateAsync: updateDocument, isPending } = useCreateOrUpdateDocument({
    onSuccess: () => {
      onSuccess?.();
      onClose();
    },
  });

  useEffect(() => {
    reset({
      filename: document?.filename || '',
    });
  }, [document?.id]);

  const onSubmit = async (data: DocumentEditFormData) => {
    await updateDocument({
      data: {
        filename: data.filename,
      },
      id: document.id.toString(),
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      placement="center"
      isDismissable={!isPending}
    >
      <ModalContent>
        <form onSubmit={handleFormSubmit(onSubmit)}>
          <ModalHeader>Edit Document</ModalHeader>
          <ModalBody className="gap-4">
            <Input
              {...register('filename')}
              label="Name"
              placeholder="Enter document name"
              isRequired
              errorMessage={errors.filename?.message}
              isInvalid={!!errors.filename}
            />
          </ModalBody>
          <ModalFooter className="justify-between border-t mt-4">
            <Button variant="bordered" onPress={onClose} isDisabled={isPending}>
              Cancel
            </Button>
            <Button color="primary" type="submit" isLoading={isPending}>
              Save Changes
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};

export default DocumentEditDrawer;
